import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTheme } from "styled-components";
import {
  ConstrainMode,
  DetailsListLayoutMode,
  DetailsRow,
  IColumn,
  IDetailsColumnRenderTooltipProps,
  IDetailsHeaderProps,
  IRenderFunction,
  ScrollablePane,
  Selection,
  SelectionMode,
  ShimmeredDetailsList,
  Sticky,
  StickyPositionType,
  TooltipHost,
  mergeStyleSets,
} from "@fluentui/react";
import { useConst } from "@fluentui/react-hooks";
import { IStyledTheme } from "theme/types";
import { useSelector } from "../../store/hooks";
import { AppDispatch } from "../../store/store";
import { RecommendationAzure } from "../../types/types";
import {
  loadEditableRecommendationsActions,
  setEditableSortRecommendations,
  selectAllEditableRecommendations,
  setModifiedRows,
  removeModifiedRow,
  EditableRecommendation,
} from "../../store/recommendationsEditable/recommendationsEditable";
import { Navigation } from "./navigation/Navigation";
import { BulkUpdateDialog } from "./BulkUpdateDialog";

import {
  getRecommendationStatusCircleColor,
  recommendationStatusColors,
} from "../../config/colors";
import { Icon } from "@fluentui/react";
import "./RecommendationsEditable.scss";

export const RecommendationsEditable = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const theme = useTheme() as IStyledTheme;
  const classes = getClassNames(theme);

  const recommendations = useSelector(selectAllEditableRecommendations);
  const totalCount = useSelector((s) => s.recommendationsEditable.count);
  const isLoading = useSelector((s) => s.recommendationsEditable.isLoading);
  const modifiedRows = useSelector(
    (s) => s.recommendationsEditable.modifiedRows,
  );
  const sort = useSelector((s) => s.recommendationsEditable.sort);
  const taxonomy = useSelector((s) => s.taxonomy);

  const [rows, setRows] = useState<EditableRecommendation[]>([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const [isBulkUpdateDialogOpen, setIsBulkUpdateDialogOpen] = useState(false);

  // Helper to compare values with type coercion (handles string/number mismatch)
  const valuesAreEqual = (a: any, b: any): boolean => {
    if (a === b) return true;
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    // Compare as strings to handle number/string type mismatch from API vs dropdown
    return String(a) === String(b);
  };

  // Initialize rows from recommendations and modifiedRows
  useEffect(() => {
    console.log(
      "useEffect triggered. modifiedRows keys:",
      Object.keys(modifiedRows),
    );

    const mergedRows = recommendations.map((rec) => {
      // modifiedRows keys are numbers but might be stored as strings in Redux
      const modified =
        modifiedRows[rec.recommendationId] ||
        modifiedRows[String(rec.recommendationId)];

      if (modified) {
        console.log(
          "Found modified row for",
          rec.recommendationId,
          "_originalValues:",
          modified._originalValues,
        );

        // Check if there are actual changes by comparing current values with original values
        const hasActualChanges = modified._originalValues
          ? Object.keys(modified._originalValues).some((key) => {
              const currentValue = (modified as any)[key];
              const originalValue = modified._originalValues?.[key];
              return !valuesAreEqual(currentValue, originalValue);
            })
          : false;

        return {
          ...modified,
          _isModified: hasActualChanges,
        };
      }
      return { ...rec, _isModified: false };
    });

    // Sort the rows based on current sort state
    const sortedRows = [...mergedRows].sort((a, b) => {
      const aValue = (a as any)[sort.key];
      const bValue = (b as any)[sort.key];

      // Handle null/undefined values - push them to the end
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Compare values
      let comparison = 0;
      if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        comparison = aValue === bValue ? 0 : aValue ? 1 : -1;
      } else {
        if (aValue < bValue) comparison = -1;
        else if (aValue > bValue) comparison = 1;
      }

      return sort.dir === "asc" ? comparison : -comparison;
    });

    setRows(sortedRows);
  }, [recommendations, modifiedRows, sort]);

  // Load recommendations on mount
  useEffect(() => {
    // Load all items at once to match what was visible on the Recommendations page
    dispatch(
      loadEditableRecommendationsActions.trigger({
        skip: 0,
        top: totalCount || 10000, // Load all items, with a high default if count not available yet
        reset: true,
      }),
    );
  }, [dispatch, totalCount]);

  const selection = useConst(
    () =>
      new Selection({
        onSelectionChanged: () => {
          setSelectedCount(selection.getSelectedCount());
        },
      }),
  );

  // Helper function to render a cell with tooltip showing old/new values
  const renderCellWithTooltip = (
    item: EditableRecommendation,
    fieldName: string,
    displayValue: string | number,
    originalDisplayValue?: string | number,
  ) => {
    const hasOriginalValue =
      item._originalValues && fieldName in item._originalValues;
    const oldValue = originalDisplayValue ?? item._originalValues?.[fieldName];
    const currentValue = (item as any)[fieldName];

    // Check if the value actually changed from original (using type-safe comparison)
    const valueActuallyChanged =
      hasOriginalValue &&
      !valuesAreEqual(currentValue, item._originalValues?.[fieldName]);

    const oldValueDisplay =
      oldValue === null || oldValue === undefined || oldValue === ""
        ? t("ram.notSet.label")
        : oldValue;
    const tooltipContent = valueActuallyChanged
      ? `${oldValueDisplay} → ${displayValue}`
      : undefined;

    if (!valueActuallyChanged) {
      return <div>{displayValue}</div>;
    }

    return (
      <TooltipHost content={tooltipContent}>
        <div
          style={{
            fontWeight: "bold",
            color: "#0078d4",
          }}
        >
          {displayValue}
        </div>
      </TooltipHost>
    );
  };

  const columns = useMemo<IColumn[]>(() => {
    const cols: IColumn[] = [
      {
        key: "recommendationStatusCode",
        name: "",
        fieldName: "recommendationStatusCode",
        minWidth: 40,
        maxWidth: 40,
        isResizable: false,
        isSorted: sort.key === "recommendationStatusCode",
        isSortedDescending: sort.dir === "desc",
        onRender: (item: EditableRecommendation) => {
          const code =
            taxonomy.RiskRecommendationStatus?.byId?.[
              item.recommendationStatusCode
            ]?.code;
          const color = getRecommendationStatusCircleColor(item, code);
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon
                iconName="CircleFill"
                style={{
                  color: color || recommendationStatusColors[code] || "#888",
                  fontSize: "12px",
                }}
              />
            </div>
          );
        },
      },
      {
        key: "title",
        name: t("ram.recommendations.column.title.label"),
        fieldName: "title",
        minWidth: 250,
        maxWidth: 400,
        isResizable: true,
        isSorted: sort.key === "title",
        isSortedDescending: sort.dir === "desc",
        onRender: (item: EditableRecommendation) => {
          return (
            <div style={{ fontWeight: item._isModified ? "bold" : "normal" }}>
              {item.title}
            </div>
          );
        },
      },
      {
        key: "improvementPotentialCode",
        name: t("ram.recommendations.column.improvementPotentialCode.label"),
        fieldName: "improvementPotentialCode",
        minWidth: 150,
        maxWidth: 200,
        isResizable: true,
        isSorted: sort.key === "improvementPotentialCode",
        isSortedDescending: sort.dir === "desc",
        onRender: (item: EditableRecommendation) => {
          const taxonomyItem =
            taxonomy.RiskImprovementPotential?.byId?.[
              item.improvementPotentialCode
            ];
          const displayValue = taxonomyItem ? t(taxonomyItem.code) : "";
          const originalValue = item._originalValues?.improvementPotentialCode;
          const originalTaxonomyItem = originalValue
            ? taxonomy.RiskImprovementPotential?.byId?.[originalValue]
            : undefined;
          const originalDisplayValue = originalTaxonomyItem
            ? t(originalTaxonomyItem.code)
            : undefined;
          return renderCellWithTooltip(
            item,
            "improvementPotentialCode",
            displayValue,
            originalDisplayValue,
          );
        },
      },
      {
        key: "statusCompanyCode",
        name: t("ram.recommendations.column.statusCompanyCode.label"),
        fieldName: "statusCompanyCode",
        minWidth: 150,
        maxWidth: 200,
        isResizable: true,
        isSorted: sort.key === "statusCompanyCode",
        isSortedDescending: sort.dir === "desc",
        onRender: (item: EditableRecommendation) => {
          const taxonomyItem =
            taxonomy.RiskImplementationStatus?.byId?.[item.statusCompanyCode];
          const displayValue = taxonomyItem ? t(taxonomyItem.code) : "";
          const originalValue = item._originalValues?.statusCompanyCode;
          const originalTaxonomyItem = originalValue
            ? taxonomy.RiskImplementationStatus?.byId?.[originalValue]
            : undefined;
          const originalDisplayValue = originalTaxonomyItem
            ? t(originalTaxonomyItem.code)
            : undefined;
          return renderCellWithTooltip(
            item,
            "statusCompanyCode",
            displayValue,
            originalDisplayValue,
          );
        },
      },
      {
        key: "typeCode",
        name: t("ram.recommendations.column.typeCode.label"),
        fieldName: "typeCode",
        minWidth: 130,
        maxWidth: 180,
        isResizable: true,
        isSorted: sort.key === "typeCode",
        isSortedDescending: sort.dir === "desc",
        onRender: (item: EditableRecommendation) => {
          const taxonomyItem =
            taxonomy.RiskCategoryOfMeasure?.byId?.[item.typeCode];
          const displayValue = taxonomyItem ? t(taxonomyItem.code) : "";
          const originalValue = item._originalValues?.typeCode;
          const originalTaxonomyItem = originalValue
            ? taxonomy.RiskCategoryOfMeasure?.byId?.[originalValue]
            : undefined;
          const originalDisplayValue = originalTaxonomyItem
            ? t(originalTaxonomyItem.code)
            : undefined;
          return renderCellWithTooltip(
            item,
            "typeCode",
            displayValue,
            originalDisplayValue,
          );
        },
      },
      {
        key: "subCategory1Code",
        name: t("ram.recommendations.column.subCategory1Code.label"),
        fieldName: "subCategory1Code",
        minWidth: 150,
        maxWidth: 200,
        isResizable: true,
        isSorted: sort.key === "subCategory1Code",
        isSortedDescending: sort.dir === "desc",
        onRender: (item: EditableRecommendation) => {
          const taxonomyItem =
            taxonomy.RiskAssessmentSubcategory?.byId?.[item.subCategory1Code];
          const displayValue = taxonomyItem ? t(taxonomyItem.code) : "";
          const originalValue = item._originalValues?.subCategory1Code;
          const originalTaxonomyItem = originalValue
            ? taxonomy.RiskAssessmentSubcategory?.byId?.[originalValue]
            : undefined;
          const originalDisplayValue = originalTaxonomyItem
            ? t(originalTaxonomyItem.code)
            : undefined;
          return renderCellWithTooltip(
            item,
            "subCategory1Code",
            displayValue,
            originalDisplayValue,
          );
        },
      },
      {
        key: "weighting",
        name: t("ram.recommendations.column.weighting.label"),
        fieldName: "weighting",
        minWidth: 100,
        maxWidth: 120,
        isResizable: true,
        isSorted: sort.key === "weighting",
        isSortedDescending: sort.dir === "desc",
        onRender: (item: EditableRecommendation) => {
          return renderCellWithTooltip(item, "weighting", item.weighting);
        },
      },
      {
        key: "priorityCompanyCode",
        name: t("ram.recommendations.column.priorityCompanyCode.label"),
        fieldName: "priorityCompanyCode",
        minWidth: 130,
        maxWidth: 180,
        isResizable: true,
        isSorted: sort.key === "priorityCompanyCode",
        isSortedDescending: sort.dir === "desc",
        onRender: (item: EditableRecommendation) => {
          if (!item.priorityCompanyCode) return <div></div>;
          const taxonomyItem =
            taxonomy.RiskPriority?.byId?.[item.priorityCompanyCode];
          const displayValue = taxonomyItem ? t(taxonomyItem.code) : "";
          const originalValue = item._originalValues?.priorityCompanyCode;
          const originalTaxonomyItem = originalValue
            ? taxonomy.RiskPriority?.byId?.[originalValue]
            : undefined;
          const originalDisplayValue = originalTaxonomyItem
            ? t(originalTaxonomyItem.code)
            : undefined;
          return renderCellWithTooltip(
            item,
            "priorityCompanyCode",
            displayValue,
            originalDisplayValue,
          );
        },
      },
      {
        key: "priorityCode",
        name: t("ram.recommendations.column.priorityCode.label"),
        fieldName: "priorityCode",
        minWidth: 130,
        maxWidth: 180,
        isResizable: true,
        isSorted: sort.key === "priorityCode",
        isSortedDescending: sort.dir === "desc",
        onRender: (item: EditableRecommendation) => {
          const taxonomyItem = taxonomy.RiskPriority?.byId?.[item.priorityCode];
          const displayValue = taxonomyItem ? t(taxonomyItem.code) : "";
          const originalValue = item._originalValues?.priorityCode;
          const originalTaxonomyItem = originalValue
            ? taxonomy.RiskPriority?.byId?.[originalValue]
            : undefined;
          const originalDisplayValue = originalTaxonomyItem
            ? t(originalTaxonomyItem.code)
            : undefined;
          return renderCellWithTooltip(
            item,
            "priorityCode",
            displayValue,
            originalDisplayValue,
          );
        },
      },
      {
        key: "improvementPotentialCodeAsIf",
        name: t("ram.recommendationForm.improvementPotentialCodeAsIf.label"),
        fieldName: "improvementPotentialCodeAsIf",
        minWidth: 150,
        maxWidth: 200,
        isResizable: true,
        isSorted: sort.key === "improvementPotentialCodeAsIf",
        isSortedDescending: sort.dir === "desc",
        onRender: (item: EditableRecommendation) => {
          if (item.improvementPotentialCodeAsIf == null) return <div></div>;
          const taxonomyItem =
            taxonomy.RiskImprovementPotential?.byId?.[
              item.improvementPotentialCodeAsIf
            ];
          const displayValue = taxonomyItem ? t(taxonomyItem.code) : "";
          const originalValue =
            item._originalValues?.improvementPotentialCodeAsIf;
          const originalTaxonomyItem = originalValue
            ? taxonomy.RiskImprovementPotential?.byId?.[originalValue]
            : undefined;
          const originalDisplayValue = originalTaxonomyItem
            ? t(originalTaxonomyItem.code)
            : undefined;
          return renderCellWithTooltip(
            item,
            "improvementPotentialCodeAsIf",
            displayValue,
            originalDisplayValue,
          );
        },
      },
      {
        key: "costEstimateCode",
        name: t("ram.costEstimate"),
        fieldName: "costEstimateCode",
        minWidth: 120,
        maxWidth: 150,
        isResizable: true,
        isSorted: sort.key === "costEstimateCode",
        isSortedDescending: sort.dir === "desc",
        onRender: (item: EditableRecommendation) => {
          if (!item.costEstimateCode) return <div></div>;
          const taxonomyItem =
            taxonomy.RiskReductionCost?.byId?.[item.costEstimateCode];
          const displayValue = taxonomyItem ? t(taxonomyItem.code) : "";
          const originalValue = item._originalValues?.costEstimateCode;
          const originalTaxonomyItem = originalValue
            ? taxonomy.RiskReductionCost?.byId?.[originalValue]
            : undefined;
          const originalDisplayValue = originalTaxonomyItem
            ? t(originalTaxonomyItem.code)
            : undefined;
          return renderCellWithTooltip(
            item,
            "costEstimateCode",
            displayValue,
            originalDisplayValue,
          );
        },
      },
      {
        key: "hideFromInsurer",
        name: t("ram.recommendations.column.hideFromInsurer.label"),
        fieldName: "hideFromInsurer",
        minWidth: 120,
        maxWidth: 150,
        isResizable: true,
        isSorted: sort.key === "hideFromInsurer",
        isSortedDescending: sort.dir === "desc",
        onRender: (item: EditableRecommendation) => {
          const displayValue =
            (item.hideFromInsurer ?? true) ? t("greco.yes") : t("greco.no");
          return renderCellWithTooltip(item, "hideFromInsurer", displayValue);
        },
      },
      {
        key: "comment",
        name: t("ram.recommendations.column.comment.label"),
        fieldName: "comment",
        minWidth: 200,
        maxWidth: 400,
        isResizable: true,
        isSorted: sort.key === "comment",
        isSortedDescending: sort.dir === "desc",
        onRender: (item: EditableRecommendation) => {
          const displayValue = item.comment || "";
          return renderCellWithTooltip(item, "comment", displayValue);
        },
      },
    ];

    return cols;
  }, [t, taxonomy, sort]);

  const onRenderRow = (props: any) => {
    if (props) {
      const customStyles = props.item._isModified
        ? { root: { backgroundColor: theme.palette?.neutralLight } }
        : undefined;
      return <DetailsRow {...props} styles={customStyles} />;
    }
    return null;
  };

  const onRenderColumnHeaderTooltip: IRenderFunction<
    IDetailsColumnRenderTooltipProps
  > = (tooltipHostProps) => {
    return (
      <TooltipHost
        {...tooltipHostProps}
        content={tooltipHostProps?.column?.name}
      />
    );
  };

  const onRenderDetailsHeader = (
    props: IDetailsHeaderProps | undefined,
    defaultRender: IRenderFunction<IDetailsHeaderProps> | undefined,
  ) => (
    <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced>
      {defaultRender!({
        ...props,
        styles: { root: { paddingTop: 0 } },
        onRenderColumnHeaderTooltip,
      })}
    </Sticky>
  );

  const _getKey = (item: EditableRecommendation): string => {
    return item?.recommendationId
      ? String(item.recommendationId)
      : String(Math.random());
  };

  const handleBulkUpdate = (updates: Record<string, any>) => {
    const selectedItems = selection.getSelection() as EditableRecommendation[];
    console.log("Bulk update called with updates:", updates);
    console.log("Selected items count:", selectedItems.length);

    selectedItems.forEach((item) => {
      // Get the original recommendation from Redux store (not the potentially modified item)
      const originalRec = recommendations.find(
        (r) => r.recommendationId === item.recommendationId,
      );
      if (!originalRec) {
        console.log("Original rec not found for:", item.recommendationId);
        return;
      }

      // Build original values for the fields we're updating
      const originalValues: Record<string, any> = {
        ...(item._originalValues || {}),
      };
      Object.keys(updates).forEach((key) => {
        // Only store original if we haven't already stored it
        if (!(key in originalValues)) {
          originalValues[key] = (originalRec as any)[key];
        }
      });

      console.log(
        "Original values for item",
        item.recommendationId,
        ":",
        originalValues,
      );

      const updatedItem: EditableRecommendation = {
        ...item,
        ...updates,
        _originalValues: originalValues,
        _isModified: true,
      };

      // Check if all updated fields match the original values (using type-safe comparison)
      const hasActualChanges = Object.keys(originalValues).some((key) => {
        const currentValue = (updatedItem as any)[key];
        const originalValue = originalValues[key];
        return !valuesAreEqual(currentValue, originalValue);
      });

      console.log("Updated item _originalValues:", updatedItem._originalValues);
      console.log("Has actual changes:", hasActualChanges);

      if (hasActualChanges) {
        dispatch(
          setModifiedRows({
            recommendationId: item.recommendationId,
            data: updatedItem,
          }),
        );
      } else {
        // Remove from modified rows if values match original
        dispatch(removeModifiedRow(item.recommendationId));
      }
    });
  };

  const hasModifications = Object.keys(modifiedRows).length > 0;

  return (
    <div className={classes.pageContainer}>
      <Navigation
        hasModifications={hasModifications}
        selectedCount={selectedCount}
        totalCount={rows.length}
        onBulkUpdate={() => setIsBulkUpdateDialogOpen(true)}
      />
      <BulkUpdateDialog
        isOpen={isBulkUpdateDialogOpen}
        onDismiss={() => setIsBulkUpdateDialogOpen(false)}
        onApply={handleBulkUpdate}
        selectedCount={selectedCount}
      />
      <div className={classes.container}>
        <ScrollablePane>
          <ShimmeredDetailsList
            compact={false}
            columns={columns}
            items={rows.filter((row) => row !== undefined && row !== null)}
            selectionMode={SelectionMode.multiple}
            setKey="multiple"
            isSelectedOnFocus
            selection={selection}
            getKey={_getKey}
            layoutMode={DetailsListLayoutMode.justified}
            enableShimmer={isLoading && rows.length === 0}
            shimmerLines={20}
            selectionPreservedOnEmptyClick={true}
            onColumnHeaderClick={(ev, column: any) => {
              if (!column) return;
              const isSorted = sort.key === column.key;
              if (isSorted) {
                if (sort.dir === "asc") {
                  dispatch(
                    setEditableSortRecommendations({
                      key: column.key,
                      dir: "desc",
                    }),
                  );
                } else {
                  dispatch(
                    setEditableSortRecommendations({
                      key: "recommendationCreatedAt",
                      dir: "desc",
                    }),
                  );
                }
              } else {
                dispatch(
                  setEditableSortRecommendations({
                    key: column.key,
                    dir: "asc",
                  }),
                );
              }
            }}
            enterModalSelectionOnTouch={true}
            ariaLabelForSelectionColumn="Toggle selection"
            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
            checkButtonAriaLabel="Row checkbox"
            constrainMode={ConstrainMode.unconstrained}
            onRenderRow={onRenderRow}
            onRenderDetailsHeader={onRenderDetailsHeader}
          />
        </ScrollablePane>
      </div>
    </div>
  );
};

const getClassNames = (theme: IStyledTheme) =>
  mergeStyleSets({
    pageContainer: {
      display: "flex",
      flexDirection: "column",
      height: "calc(100vh - 50px)",
      width: "100%",
    },
    container: {
      height: "calc(100vh - 100px)",
      marginBottom: "20px",
      position: "relative",
      overflow: "auto",
      width: "100%",
    },
    table: {
      ".ms-List-cell:nth-child(odd) .ms-DetailsRow": {
        background: theme.isDark
          ? "rgb(2, 7, 10)"
          : theme.palette?.themeLighterAlt,
      },
      ".ms-DetailsRow": {
        height: "30px !important",
      },
      ".ms-DetailsHeader-cellTitle": {
        justifyContent: "flex-start",
      },
      ".ms-DetailsRow-cell": {
        display: "flex",
        justifyContent: "flex-start",
      },
    },
  });
